/* eslint no-underscore-dangle: ["error", { "allow": ["_id", "_f"] }] */
import { useState, useContext, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { IContactEditInfo } from './types';
import { updateContactInfo } from '../../../services/updateContactInfo';
import DrawerContext from '../../../contexts/drawer/context';
import { createYupSchema } from '../../../helpers/yupSchemaCreator';
import { searchUser } from '../../../services/contactSearch';
import { getLiveChatUsersAgent } from '../../../services/getLivechatUsersAgt';
import { User, getPhoneNumber } from '../../../services/types';
import { getUserInfo } from '../../../services/getUserInfo';
import { sanitizePhoneDigits } from '../../../helpers/phone';

const useEdit = ({ token, handleEditing, customFields, handleIsDirtyForm, isBSUIDContact }: any) => {
  const [stateSchema, setStateSchema] = useState(yup.object().shape({}));
  const [agents, setAllAgents] = useState<User[]>([]);
  const [contactManager, setContactManager] = useState<User>();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    setError,
    getValues,
    clearErrors,
  } = useForm<IContactEditInfo>({
    mode: 'onBlur',
    resolver: yupResolver(stateSchema),
  });

  useEffect(() => {
    if (customFields) {
      const yepSchema = customFields.reduce(createYupSchema, {});
      const validateSchema = yup.object().shape(yepSchema);
      setStateSchema(validateSchema);
    }
  }, [customFields]);
  const { loadUserInfo, contactInfo } = useContext(DrawerContext);

  useEffect(() => {
    let isMounted = true;
    const getAgents = async () => {
      const {
        data: { users },
      } = await getLiveChatUsersAgent();

      if (isMounted) {
        setAllAgents(users);
      }
    };

    getAgents();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const getContactManager = async (username?: string) => {
      if (!username) {
        setContactManager(undefined);
        setValue('agentResponsibleForThisContact', undefined);
        return;
      }

      const {
        data: { user },
      } = await getUserInfo(username);

      if (isMounted) {
        setContactManager(user);
        setValue('agentResponsibleForThisContact', user._id, { shouldDirty: false });
      }
    };

    getContactManager(contactInfo?.contactManager?.username);

    return () => {
      isMounted = false;
    };
  }, [contactInfo?.contactManager?.username]);

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [errorUpdt, setErrorUpdt] = useState(false);
  const [errorDocNumber, setErrorDocNumber] = useState('');

  useEffect(() => {
    if (handleIsDirtyForm) {
      handleIsDirtyForm(isDirty);
    }
  }, [isDirty]);

  const onSubmit: SubmitHandler<IContactEditInfo> = async (form) => {
    setLoading(true);
    try {
      if (!form.name) {
        setError('name', {
          type: 'required',
          message: 'fieldIsRequired',
        });
        return;
      }
      const { _fields } = control;
      if (contactInfo) {
        const previousTags = _fields.tags?._f.value;
        if (!form.tags && previousTags) {
          form.tags = previousTags;
        }

        if (contactInfo._id) {
          const { name, email, agentResponsibleForThisContact, phone, ...customFieldsToSend } = form;
          const foundContactManager = agents.find((agent) => agent._id === agentResponsibleForThisContact);
          const phoneToSend = isBSUIDContact
            ? sanitizePhoneDigits(phone) || undefined
            : getPhoneNumber(contactInfo.phone);
          const { data } = await updateContactInfo(
            contactInfo._id,
            token,
            name,
            email,
            phoneToSend,
            customFieldsToSend,
            {
              username: foundContactManager?.username ?? '',
            }
          );

          if (data.success) {
            loadUserInfo(contactInfo._id);
            setLoading(false);
            handleEditing(true);
          }
        }
      }
    } catch (error: any) {
      setErrorUpdt(true);
      setLoading(false);
      console.log('error UPDT', error);
    }
  };

  const checkField = async (field: string) => {
    if (field === 'email') {
      const { data: contact } = await searchUser(field, getValues(field));
      if (contact.contact) {
        setError('email', {
          type: 'required',
          message: 'emailAlreadyExists',
        });
      } else {
        clearErrors();
        setValue('email', getValues(field));
      }
    }
  };

  return {
    control,
    handleSubmit,
    translation: t,
    onSubmit,
    errors,
    errorUpdt,
    loading,
    setValue,
    errorDocNumber,
    setErrorDocNumber,
    checkField,
    agents,
    contactManager,
  };
};

export default useEdit;
